import {
  type AriaAttributes,
  createContext,
  type KeyboardEventHandler,
  type ReactNode,
  use,
  useCallback,
  useId,
} from 'react'
import {
  WizardContentFooter,
  WizardContentSentinelHeader,
} from '@/components/Wizard'
import { AnimatePresence, LazyMotion, m } from 'motion/react'
import {
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  DisclosureStateContext,
  Heading as AriaHeading,
} from 'react-aria-components'
import styles from './CheckoutView.module.css'
import { Button, RawButton } from '@/components/Button'
import flattenChildren from 'react-keyed-flatten-children'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { ScreenHeaderTitle } from '@/components/ScreenHeader'
import { staticTransition } from '@/consts/springs'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  Card,
  CardButton,
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { IconButton } from '@/components/IconButton'
import { TextField, TextFieldSlot } from '@/components/inputs/TextField'
import { FormField, FormFieldHint, useFormField } from '@/components/Form'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  AutoSkeleton,
  IsSkeletonContext,
  SkeletonColorContext,
  SkeletonList,
  SkeletonOverlay,
  useIsSkeleton,
  useSkeletonColor,
} from '@/components/Skeleton'
import { match } from '@/utils/pattern-match-util'
import { LabelChip } from '@/components/Chip'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

interface CheckoutViewProps {
  children?: ReactNode
  summary?: ReactNode
  footer?: ReactNode
  testId?: string
}

/**
 * Checkout View
 */
export function CheckoutView({
  summary,
  footer,
  children,
  testId,
}: CheckoutViewProps) {
  const isSpacious = useIsSpacious()
  return (
    <LazyMotion features={loadMotionFeatures}>
      <WizardContentSentinelHeader />
      <main className={styles.root} data-test={testId}>
        <div className={styles.rootLayout}>
          {isSpacious && flattenChildren(summary).length > 0 ? (
            <>
              <div className={styles.columnLeft}>{children}</div>
              <div className={styles.columnRight}>
                {summary}
                {footer}
              </div>
            </>
          ) : (
            <>
              {summary}
              {children}
              {footer}
            </>
          )}
        </div>
      </main>
    </LazyMotion>
  )
}

interface CheckoutViewMainProps {
  children?: ReactNode
}

/**
 * Checkout View Main
 */
export function CheckoutViewMain({ children }: CheckoutViewMainProps) {
  return <div className={styles.main}>{children}</div>
}

interface CheckoutViewMainHeaderProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Checkout View Main Header
 */
export function CheckoutViewMainHeader({
  children,
  isSkeleton,
}: CheckoutViewMainHeaderProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <header className={styles.mainHeader}>
      <IsSkeletonContext value={isSkeleton}>{children}</IsSkeletonContext>
    </header>
  )
}

interface CheckoutViewMainHeaderTitleProps {
  children?: ReactNode
}

/**
 * Checkout View Main Header Title
 */
export function CheckoutViewMainHeaderTitle({
  children,
}: CheckoutViewMainHeaderTitleProps) {
  return (
    <ScreenHeaderTitle>
      <h1 className={styles.mainHeaderTitle}>
        <AutoSkeleton>{children}</AutoSkeleton>
      </h1>
    </ScreenHeaderTitle>
  )
}

interface CheckoutViewMainHeaderContentProps {
  children?: ReactNode
}

/**
 * Checkout View Main Header Content
 */
export function CheckoutViewMainHeaderContent({
  children,
}: CheckoutViewMainHeaderContentProps) {
  return <div className={styles.mainHeaderContent}>{children}</div>
}

interface CheckoutViewMainHeaderBulletListProps {
  children?: ReactNode
}

/**
 * Checkout View Main Header Bullet List
 */
export function CheckoutViewMainHeaderBulletList({
  children,
}: CheckoutViewMainHeaderBulletListProps) {
  return <ul className={styles.mainHeaderBulletList}>{children}</ul>
}

interface CheckoutViewMainHeaderBulletListItemProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Checkout View Main Header Bullet List Item
 */
export function CheckoutViewMainHeaderBulletListItem({
  children,
  isSkeleton,
}: CheckoutViewMainHeaderBulletListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <li className={styles.mainHeaderBulletListItem}>
      <Icon skeleton={isSkeleton} sm variant="active" name="radio-check" />

      <span>
        <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
      </span>
    </li>
  )
}

interface CheckoutViewMainContentProps {
  children?: ReactNode
}

/**
 * Checkout View Main Content
 */
export function CheckoutViewMainContent({
  children,
}: CheckoutViewMainContentProps) {
  return <div className={styles.mainContent}>{children}</div>
}

const InternalCheckoutViewSummaryTitleIdContext = createContext<
  string | undefined
>(undefined)

interface CheckoutViewSummaryProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Checkout View Summary
 */
export function CheckoutViewSummary({
  isSkeleton,
  children,
}: CheckoutViewSummaryProps) {
  const titleId = useId()
  const isSpacious = useIsSpacious()
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <section
      className={cx(styles.summary, isSkeleton && styles.skeleton)}
      aria-labelledby={titleId}
    >
      <SkeletonColorContext value={isSpacious ? 'primary' : 'secondary'}>
        <InternalCheckoutViewSummaryTitleIdContext value={titleId}>
          <AriaDisclosure
            defaultExpanded={isSpacious}
            className={({ isExpanded }) =>
              cx(styles.summaryLayout, isExpanded && styles.expanded)
            }
          >
            {children}
          </AriaDisclosure>
        </InternalCheckoutViewSummaryTitleIdContext>
      </SkeletonColorContext>
    </section>
  )
}

interface CheckoutViewSummaryHeaderProps {
  detail?: ReactNode
  skeleton?: 'full' | 'details-only' | false
  title?: string
  testId?: string
}

/**
 * Checkout View Summary Header
 */
export function CheckoutViewSummaryHeader({
  detail,
  skeleton,
  testId,
  title = 'Summary',
}: CheckoutViewSummaryHeaderProps) {
  const titleId = use(InternalCheckoutViewSummaryTitleIdContext)
  const { isExpanded } = use(DisclosureStateContext) ?? {}
  return (
    <AriaHeading level={3} className={styles.summaryHeader} id={titleId}>
      <RawButton
        slot="trigger"
        isDisabled={skeleton === 'full'}
        // Don't add test ID while loading to make sure the test code
        // waits for loading to be finished.
        testId={skeleton === 'full' ? undefined : testId}
        className={({ isFocusVisible }) =>
          cx(
            styles.summaryHeaderLabel,
            isExpanded && styles.expanded,
            isFocusVisible && styles.focusVisible,
          )
        }
      >
        <AutoSkeleton isSkeleton={skeleton === 'full'}>{title}</AutoSkeleton>
        <DisclosureIndicatorIcon
          isSkeleton={skeleton === 'full'}
          isExpanded={isExpanded ?? false}
        />
      </RawButton>
      <AnimatePresence initial={false}>
        {detail && !isExpanded && (
          <m.div
            className={styles.summaryHeaderDetail}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { opacity: 0, y: 2, transition: { duration: 0.1 } },
              visible: { opacity: 1, y: 0 },
            }}
          >
            <AutoSkeleton isSkeleton={!!skeleton}>{detail}</AutoSkeleton>
          </m.div>
        )}
      </AnimatePresence>
    </AriaHeading>
  )
}

interface CheckoutViewSummaryContentProps {
  children?: ReactNode
}

/**
 * Checkout View Summary Content
 */
export function CheckoutViewSummaryContent({
  children,
}: CheckoutViewSummaryContentProps) {
  const { isExpanded } = use(DisclosureStateContext) ?? {}
  return (
    <AriaDisclosurePanel
      className={cx(
        styles.summaryContent,
        !isExpanded && styles.disclosureHidden,
      )}
    >
      {children}
    </AriaDisclosurePanel>
  )
}

interface CheckoutViewSummaryContentListProps {
  children?: ReactNode
}

/**
 * Checkout View Summary Content List
 */
export function CheckoutViewSummaryContentList({
  children,
}: CheckoutViewSummaryContentListProps) {
  return <ul className={styles.summaryContentList}>{children}</ul>
}

interface CheckoutViewSummaryContentListQuoteItemProps extends Pick<
  AriaAttributes,
  'aria-live' | 'aria-label' | 'aria-atomic'
> {
  label: ReactNode
  extras?: ReactNode
  detail?: ReactNode
  emphasized?: boolean
  isSkeleton?: boolean
  alwaysExpanded?: boolean
  children?: ReactNode
  labelTestId?: string
  detailTestId?: string
}

/**
 * Summary content list item for quote line items.
 */
export function CheckoutViewSummaryContentListQuoteItem({
  label,
  extras,
  detail,
  emphasized,
  children,
  alwaysExpanded,
  isSkeleton,
  labelTestId,
  detailTestId,
  ...rest
}: CheckoutViewSummaryContentListQuoteItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const flattenedChildren = flattenChildren(children)
  const headerClassName = cx(
    styles.summaryContentListItemHeader,
    emphasized && styles.emphasized,
  )

  const itemElements = (
    <>
      {flattenChildren(extras).length > 0 && (
        <div className={styles.summaryContentListItemExtras}>{extras}</div>
      )}

      <div
        className={styles.summaryContentListItemDetail}
        data-test={detailTestId}
      >
        <AutoSkeleton isSkeleton={isSkeleton}>{detail}</AutoSkeleton>
      </div>
    </>
  )

  const hasChildren = flattenedChildren.length > 0

  return (
    <li className={styles.summaryContentListItem} {...rest}>
      {hasChildren && !alwaysExpanded ? (
        // Make it a disclosure when there are children.
        <AriaDisclosure>
          {({ isExpanded }) => (
            <>
              <div className={headerClassName}>
                <RawButton
                  slot="trigger"
                  testId={labelTestId}
                  className={({ isFocusVisible }) =>
                    cx(
                      styles.summaryContentListItemLabel,
                      isFocusVisible && styles.focusVisible,
                    )
                  }
                >
                  <AutoSkeleton isSkeleton={isSkeleton}>{label}</AutoSkeleton>
                  <DisclosureIndicatorIcon
                    isSkeleton={isSkeleton}
                    isExpanded={isExpanded}
                  />
                </RawButton>

                {itemElements}
              </div>
              <AriaDisclosurePanel
                className={cx(!isExpanded && styles.disclosureHidden)}
              >
                {children}
              </AriaDisclosurePanel>
            </>
          )}
        </AriaDisclosure>
      ) : (
        <div>
          <div className={headerClassName}>
            <div
              className={styles.summaryContentListItemLabel}
              data-test={labelTestId}
            >
              <AutoSkeleton isSkeleton={isSkeleton}>{label}</AutoSkeleton>
            </div>
            {itemElements}
          </div>
          {hasChildren && <div>{children}</div>}
        </div>
      )}
    </li>
  )
}

interface CheckoutViewSummaryContentListResponseItemProps {
  children?: ReactNode
  label: string
  detail: string
  labelTestId?: string
  detailTestId?: string
}

/**
 * Checkout View Summary Content List Response Item
 */
export function CheckoutViewSummaryContentListResponseItem({
  label,
  detail,
  labelTestId,
  detailTestId,
}: CheckoutViewSummaryContentListResponseItemProps) {
  return (
    <li className={styles.summaryContentListResponseItem}>
      <div
        className={styles.summaryContentListResponseItemLabel}
        data-test={labelTestId}
      >
        {label}
      </div>
      <div
        className={styles.summaryContentListResponseItemDetail}
        data-test={detailTestId}
      >
        {detail}
      </div>
    </li>
  )
}

interface CheckoutViewSummaryContentListItemSublistProps {
  children?: ReactNode
}

/**
 * Checkout View Summary Content List Item Sublist
 */
export function CheckoutViewSummaryContentListItemSublist({
  children,
}: CheckoutViewSummaryContentListItemSublistProps) {
  return <ul className={styles.summaryContentListItemSublist}>{children}</ul>
}

interface CheckoutViewSummaryContentListItemSublistItemProps {
  children?: ReactNode
  isSkeleton?: boolean
  testId?: string
}

/**
 * Checkout View Summary Content List Item Sublist Item
 */
export function CheckoutViewSummaryContentListItemSublistItem({
  children,
  isSkeleton,
  testId,
}: CheckoutViewSummaryContentListItemSublistItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <li data-test={testId} className={styles.summaryContentListItemSublistItem}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </li>
  )
}

interface CheckoutViewSummaryContentListItemNoteProps {
  children?: ReactNode
  isSkeleton?: boolean
  testId?: string
}

/**
 * Checkout View Summary Content List Item Note
 */
export function CheckoutViewSummaryContentListItemNote({
  children,
  isSkeleton,
  testId,
}: CheckoutViewSummaryContentListItemNoteProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <p className={styles.summaryContentListItemNote} data-test={testId}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </p>
  )
}

interface CheckoutViewSummaryContentDividerProps {
  isSkeleton?: boolean
}

/**
 * Checkout View Summary Content Divider
 */
export function CheckoutViewSummaryContentDivider({
  isSkeleton,
}: CheckoutViewSummaryContentDividerProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const skeletonColor = useSkeletonColor()
  return (
    <hr
      className={cx(
        styles.summaryContentDivider,
        isSkeleton &&
          match(skeletonColor, {
            primary: styles.skeletonPrimary,
            secondary: styles.skeletonSecondary,
          }),
      )}
    />
  )
}

interface CheckoutViewSummaryCouponChipProps {
  code: string
  onRemovePress?: () => unknown
  isDisabled?: boolean
  isSkeleton?: boolean
  codeTestId?: string
  removeButtonTestId?: string
}

/**
 * Checkout View Summary Coupon Chip
 */
export function CheckoutViewSummaryCouponChip({
  code,
  onRemovePress,
  isDisabled,
  isSkeleton,
  codeTestId,
  removeButtonTestId,
}: CheckoutViewSummaryCouponChipProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <div
      className={cx(styles.summaryCouponChip, isDisabled && styles.disabled)}
    >
      <span className={styles.summaryCouponChipCode} data-test={codeTestId}>
        {code}
      </span>
      <RawButton
        isDisabled={isDisabled || isSkeleton}
        onPress={onRemovePress}
        testId={removeButtonTestId}
        className={({ isFocusVisible, isDisabled, isHovered }) =>
          cx(
            styles.summaryCouponChipRemoveButton,
            isDisabled && styles.disabled,
            isFocusVisible && styles.focusVisible,
            isHovered && styles.hover,
          )
        }
      >
        <Icon mini name="close" />
      </RawButton>
      {isSkeleton && <SkeletonOverlay />}
    </div>
  )
}

interface CheckoutViewCouponSectionProps {
  children?: ReactNode
  title?: string
  isSkeleton?: boolean
}

/**
 * Checkout View Coupon Section
 */
export function CheckoutViewCouponSection({
  children,
  isSkeleton,
  title = 'Coupon',
}: CheckoutViewCouponSectionProps) {
  const titleId = useId()
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <section aria-labelledby={titleId} className={styles.couponSection}>
      <SectionHeadline isSkeleton={isSkeleton} id={titleId}>
        {title}
      </SectionHeadline>
      <div className={styles.couponSectionContent}>{children}</div>
    </section>
  )
}

interface CheckoutViewCouponSectionAppliedCouponProps {
  title: string
  description?: string
  onRemovePress?: () => unknown
  isDisabled?: boolean
  isExpired?: boolean
  titleTestId?: string
  subtitleTestId?: string
  removeButtonTestId?: string
}

/**
 * Checkout View Coupon Section Applied Coupon
 */
export function CheckoutViewCouponSectionAppliedCoupon({
  title,
  description,
  onRemovePress,
  isDisabled,
  isExpired,
  titleTestId,
  subtitleTestId,
  removeButtonTestId,
}: CheckoutViewCouponSectionAppliedCouponProps) {
  return (
    <Card isActive={!isExpired}>
      <CardItemContent
        align="center"
        extras={
          <>
            <div className="flex items-center gap-sm">
              {isExpired && <LabelChip text="Expired" variant="danger" />}
              <IconButton
                isDisabled={isDisabled}
                name="close"
                label="Remove"
                onPress={onRemovePress}
                testId={removeButtonTestId}
              />
            </div>
          </>
        }
      >
        <CardTitle overflowWrap="break-all" testId={titleTestId}>
          {title}
        </CardTitle>
        <CardSubtitle variant="label" testId={subtitleTestId}>
          {description}
        </CardSubtitle>
      </CardItemContent>
    </Card>
  )
}

interface CheckoutViewCouponSectionAvailableCouponsListProps {
  children?: ReactNode
  'aria-label'?: string
}

/**
 * Checkout View Coupon Section Available Coupons List
 */
export function CheckoutViewCouponSectionAvailableCouponsList({
  children,
  ...rest
}: CheckoutViewCouponSectionAvailableCouponsListProps) {
  return <CardList {...rest}>{children}</CardList>
}

interface CheckoutViewCouponSectionAvailableCouponsListItemProps {
  title: string
  description?: string
  onApplyPress?: () => unknown
  isDisabled?: boolean
  isPending?: boolean
  testId?: string
  titleTestId?: string
  subtitleTestId?: string
  applyButtonTestId?: string
}

/**
 * Checkout View Coupon Section Available Coupon List Item
 */
export function CheckoutViewCouponSectionAvailableCouponsListItem({
  title,
  description,
  onApplyPress,
  isDisabled,
  isPending,
  testId,
  titleTestId,
  subtitleTestId,
  applyButtonTestId,
}: CheckoutViewCouponSectionAvailableCouponsListItemProps) {
  return (
    <CardListItem textValue={title} isDisabled={isDisabled} testId={testId}>
      <CardItemContent
        align="center"
        spacingRight="sm"
        extras={
          <Button
            isDisabled={isDisabled}
            isPending={isPending}
            variant="text"
            label="Apply"
            onPress={onApplyPress}
            testId={applyButtonTestId}
          />
        }
      >
        <CardTitle testId={titleTestId}>{title}</CardTitle>
        <CardSubtitle testId={subtitleTestId} variant="label">
          {description}
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface CheckoutViewCouponCodeInputFieldProps {
  value?: string
  onChange?: (value: string) => unknown
  onAddPress?: () => unknown
  isDisabled?: boolean
  isPending?: boolean
  isSkeleton?: boolean
  error?: string
  inputTestId?: string
  applyButtonTestId?: string
}

/**
 * Checkout View Coupon Code Input Field
 */
export function CheckoutViewCouponCodeInputField({
  value,
  onChange,
  onAddPress,
  isDisabled,
  isPending,
  isSkeleton,
  error,
  inputTestId,
  applyButtonTestId,
}: CheckoutViewCouponCodeInputFieldProps) {
  const { hint, control } = useFormField({
    name: 'coupon',
    'aria-label': 'Coupon code',
    invalid: !!error,
  })

  const onTextFieldKeyDown: KeyboardEventHandler = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onAddPress?.()
      }
    },
    [onAddPress],
  )

  return (
    <FormField>
      <TextField
        {...control}
        placeholder="Enter coupon code"
        value={value}
        onChange={onChange}
        onKeyDown={onTextFieldKeyDown}
        readOnly={isDisabled}
        isSkeleton={isSkeleton}
        testId={inputTestId}
      >
        <TextFieldSlot side="right">
          <Button
            isDisabled={isDisabled || !value}
            isPending={isPending}
            onPress={onAddPress}
            isSkeleton={isSkeleton}
            label="Apply"
            variant="text"
            testId={applyButtonTestId}
          />
        </TextFieldSlot>
      </TextField>
      <FormFieldHint isSkeleton={isSkeleton} {...hint}>
        {error}
      </FormFieldHint>
    </FormField>
  )
}

interface CheckoutViewSectionSeparatorProps {
  label: string
  padded?: boolean
}

/**
 * Checkout View Coupon Section Separator
 */
export function CheckoutViewSectionSeparator({
  label,
  padded = false,
}: CheckoutViewSectionSeparatorProps) {
  return (
    <div
      className={cx(styles.sectionSeparator, padded && styles.separatorPadding)}
    >
      <hr />
      <p className={styles.sectionSeparatorLabel}>{label}</p>
    </div>
  )
}

interface CheckoutViewBillingMethodsSectionProps {
  children?: ReactNode
  isDisabled?: boolean
  isSkeleton?: boolean
  titleId?: string
  onAddMethodPress?: () => unknown
}

/**
 * Checkout View Billing Section
 */
export function CheckoutViewBillingMethodsSection({
  children,
  isDisabled,
  isSkeleton,
  titleId,
  onAddMethodPress,
}: CheckoutViewBillingMethodsSectionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const defaultTitleId = useId()
  titleId ??= defaultTitleId
  const isSpacious = useIsSpacious()
  return (
    <section aria-labelledby={titleId} className={styles.billingMethodsSection}>
      <SectionHeadline
        id={titleId}
        isSkeleton={isSkeleton}
        action={
          isSpacious && (
            <Button
              variant="text"
              label="Add payment method"
              isDisabled={isDisabled}
              onPress={onAddMethodPress}
              isSkeleton={isSkeleton}
            />
          )
        }
      >
        Choose how to pay
      </SectionHeadline>
      <div className={styles.billingMethodsList}>{children}</div>
      {!isSpacious && (
        <CardButton
          onPress={onAddMethodPress}
          isDisabled={isDisabled || isSkeleton}
        >
          <CardItemContent
            isSkeleton={isSkeleton}
            spacingY="base"
            icon={<CardItemIcon skeleton={isSkeleton} name="card" />}
            isInteractive
          >
            <CardTitle>
              <AutoSkeleton isSkeleton={isSkeleton}>
                Add payment method
              </AutoSkeleton>
            </CardTitle>
          </CardItemContent>
        </CardButton>
      )}
    </section>
  )
}

interface CheckoutViewBillingFormSectionProps {
  children?: ReactNode
}

/**
 * Checkout View Billing Form Section
 */
export function CheckoutViewBillingFormSection({
  children,
}: CheckoutViewBillingFormSectionProps) {
  return (
    // The actual payment method editor will have subheadings, but there's not
    // a single heading that would apply for this section design-wise,
    // so for a11y purposes we'll just add `aria-label`.
    <section className={styles.billingFormSection} aria-label="Payment details">
      {children}
    </section>
  )
}

interface CheckoutViewFooterProps {
  children?: ReactNode
}

/**
 * Checkout View Footer
 */
export function CheckoutViewFooter({ children }: CheckoutViewFooterProps) {
  return (
    <div className={styles.footer}>
      <WizardContentFooter>{children}</WizardContentFooter>
    </div>
  )
}

/**
 * Skeleton for {@link CheckoutViewSummary}.
 *
 * @constructor
 */
export function CheckoutViewSummarySkeleton() {
  return (
    <CheckoutViewSummary>
      <CheckoutViewSummaryHeader skeleton="full" />
      <CheckoutViewSummaryContent>
        <CheckoutViewSummaryContentList>
          <SkeletonList count={3}>
            {({ vary }) => (
              <CheckoutViewSummaryContentListQuoteItem
                label={<AutoSkeleton characters={vary(24)} />}
                detail={<AutoSkeleton characters={vary(10)} />}
              />
            )}
          </SkeletonList>
        </CheckoutViewSummaryContentList>
        <CheckoutViewSummaryContentDivider />
        <CheckoutViewSummaryContentList>
          <SkeletonList count={1}>
            <CheckoutViewSummaryContentListQuoteItem
              label={<AutoSkeleton characters={11} />}
              detail={<AutoSkeleton characters={12} />}
              emphasized
            />
          </SkeletonList>
        </CheckoutViewSummaryContentList>
      </CheckoutViewSummaryContent>
    </CheckoutViewSummary>
  )
}

interface DisclosureIndicatorIconProps {
  isExpanded: boolean
  isSkeleton?: boolean
}

/**
 * Icon for the disclosure header, indicating whether the content
 * is expanded or not.
 */
function DisclosureIndicatorIcon({
  isExpanded,
  isSkeleton,
}: DisclosureIndicatorIconProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <div className={styles.disclosureIndicatorIcon}>
      <m.div
        transition={staticTransition}
        initial={false}
        animate={
          isSkeleton
            ? false
            : {
                rotate: isExpanded ? 90 : 0,
              }
        }
      >
        <Icon skeleton={isSkeleton} name="chevron" sm />
      </m.div>
    </div>
  )
}
